/**
 * Per-pipeline identity accent.
 *
 * The console palette is deliberately indigo-on-charcoal almost everywhere; the
 * pipelines catalogue is one screen where a little colour earns its keep. Each
 * pipeline gets a **stable** accent so it keeps the same hue across renders and
 * sessions — colour as wayfinding (recognise "the cyan one"), not decoration.
 *
 * The **native Home pipeline** (`adapterKind === null`) always wears the Ludus
 * brand accent (`--color-primary`), so the tenant's own pipeline is instantly
 * recognisable. Every other (external-CRM-backed) pipeline draws a stable hue
 * from a diverse palette — the theme's curated `--color-stage-*-active` tones
 * plus one in-family extra — keyed by a deterministic hash of its id. The brand
 * indigo and red (an alarm tone elsewhere) are excluded from that palette so
 * the brand stays Home-exclusive.
 *
 * Colours are **theme tokens**, not hard-coded hex: the solid is a `var(...)`
 * reference and the tile/ring/glow alpha variants are derived with `color-mix`,
 * so a rebrand (or a stage-palette retune) propagates here automatically. Same
 * "reference the token, don't copy its value" philosophy as
 * `shared/utils/stageColors.ts`.
 */

export interface PipelineAccent {
  /** Solid accent — initial letter, chevron, focus ring. */
  hex: string
  /** Icon-tile fill (accent at low alpha). */
  tile: string
  /** Icon-tile ring + hover inset border (accent at mid alpha). */
  ring: string
  /** Corner glow wash over the card surface (accent at very low alpha). */
  glow: string
}

/** Ludus brand accent — `--color-primary`. Reserved for the Home pipeline. */
const BRAND = 'var(--color-primary)'

/** Diverse identity palette for external-CRM-backed pipelines (no brand, no red). */
const PALETTE: readonly string[] = [
  'var(--color-stage-cool-active)', // blue
  'var(--color-stage-mid-active)', // cyan
  'var(--color-accent)', // emerald
  'var(--color-stage-warm-active)', // amber
  '#8b5cf6', // violet — in-family extra, no token of its own
]

/** Small deterministic string hash (FNV-ish); stable across renders/sessions. */
function hashId(id: string): number {
  let h = 0
  for (let i = 0; i < id.length; i++) {
    h = (h * 31 + id.charCodeAt(i)) | 0
  }
  return Math.abs(h)
}

/** Build the four accent surfaces from a single colour expression (var or hex). */
function accentFrom(color: string): PipelineAccent {
  return {
    hex: color,
    tile: `color-mix(in srgb, ${color} 14%, transparent)`,
    ring: `color-mix(in srgb, ${color} 34%, transparent)`,
    glow: `color-mix(in srgb, ${color} 12%, transparent)`,
  }
}

export function pipelineAccent(pipeline: {
  id: string
  adapterKind: string | null
}): PipelineAccent {
  const color =
    pipeline.adapterKind === null ? BRAND : PALETTE[hashId(pipeline.id) % PALETTE.length]
  return accentFrom(color)
}
